/**
 * Task Service for Thaklis Pro
 * Handles internal tasks for typing centers via real API calls.
 *
 * Terminology:
 * - Ticket: Thaklis Platform ticket (visa/govt services)
 * - Task: Internal typing center task (translation, typing, courier, etc.)
 */

import ApiService from './api.service';

/**
 * Get the current user's typing center ID from localStorage.
 */
const getTypingCenterId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return user.typingCenterId || user.typing_center_id;
  } catch {
    return null;
  }
};

const TaskService = {
  /**
   * Get all tasks with optional filters.
   * @param {Object} params - Filter parameters: status, workOrderId, assignedTo, search
   * @returns {Promise<{data: Array, total: number}>}
   */
  getAll: async (params = {}) => {
    const typingCenterId = getTypingCenterId();
    const queryParams = { typingCenterId };

    if (params.status && params.status !== 'ALL') {
      queryParams.status = params.status;
    }
    if (params.workOrderId) {
      queryParams.workOrderId = params.workOrderId;
    }
    if (params.assignedTo) {
      queryParams.assignedTo = params.assignedTo;
    }
    if (params.search) {
      queryParams.search = params.search;
    }

    const response = await ApiService.get('/api/tasks', { params: queryParams });
    const data = response.data?.data || [];
    return { data, total: data.length };
  },

  /**
   * Get a single task by ID (enriched with work order / org / customer data).
   * @param {string|number} id
   * @returns {Promise<Object>}
   */
  getById: async (id) => {
    const response = await ApiService.get(`/api/tasks/${id}`);
    return response.data?.data;
  },

  getWorkOrderTasks: async (params = {}) => {
    const typingCenterId = getTypingCenterId();
    const query = { typingCenterId, ...params };
    const response = await ApiService.get('/api/tasks/work-order-tasks', { params: query });
    const pageData = response.data?.data || {};
    return {
      data: Array.isArray(pageData.content) ? pageData.content : [],
      total: pageData.total_elements ?? pageData.totalElements ?? 0
    };
  },

  getWorkOrderTaskStatistics: async (params = {}) => {
    const typingCenterId = getTypingCenterId();
    const query = { typingCenterId, ...params };
    const response = await ApiService.get('/api/tasks/work-order-tasks/statistics', { params: query });
    return response.data?.data || { total: 0, pending: 0, in_progress: 0, completed: 0 };
  },

  /**
   * Get task statistics (counts by status) for the current typing center.
   * @returns {Promise<Object>} { total, pending, inProgress, completed, unassigned }
   */
  getStatistics: async () => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/tasks/statistics', {
      params: { typingCenterId }
    });
    return response.data?.data;
  },

  /**
   * Create a new internal task.
   * @param {Object} taskData
   * @returns {Promise<Object>} Created task
   */
  create: async (taskData) => {
    const body = {
      workOrderTaskId: taskData.workOrderTaskId,
      typingCenterId: taskData.typingCenterId || getTypingCenterId(),
      taskName: taskData.taskName,
      description: taskData.description,
      notes: taskData.notes
    };

    const response = await ApiService.post('/api/tasks', body);
    return response.data?.data;
  },

  /**
   * Update a task's editable fields.
   * @param {string|number} id
   * @param {Object} updates
   * @returns {Promise<Object>} Updated task
   */
  update: async (id, updates) => {
    const response = await ApiService.put(`/api/tasks/${id}`, updates);
    return response.data?.data;
  },

  /**
   * Assign a task to an employee.
   * @param {string|number} id
   * @param {string} assignedTo - Employee ID
   * @param {string} assignedToName - Employee display name
   * @returns {Promise<Object>} Updated task
   */
  assign: async (id, assignedTo, assignedToName) => {
    const response = await ApiService.post(`/api/tasks/${id}/assign`, {
      assignedTo,
      assignedToName
    });
    return response.data?.data;
  },

  /**
   * Unassign a task.
   * @param {string|number} id
   * @returns {Promise<Object>} Updated task
   */
  unassign: async (id) => {
    const response = await ApiService.post(`/api/tasks/${id}/unassign`);
    return response.data?.data;
  },

  /**
   * Mark a task as completed.
   * @param {string|number} id
   * @param {string} completedBy
   * @returns {Promise<Object>} Updated task
   */
  complete: async (id, completedBy) => {
    const response = await ApiService.post(`/api/tasks/${id}/complete`, { completedBy });
    return response.data?.data;
  },

  /**
   * Reopen a completed task.
   * @param {string|number} id
   * @returns {Promise<Object>} Updated task
   */
  reopen: async (id) => {
    const response = await ApiService.post(`/api/tasks/${id}/reopen`);
    return response.data?.data;
  },

  /**
   * Delete a task.
   * @param {string|number} id
   * @returns {Promise<Object>}
   */
  delete: async (id) => {
    const response = await ApiService.delete(`/api/tasks/${id}`);
    return response.data?.data || { success: true };
  },

  // ==================== Display Utilities ====================

  /**
   * Get MUI Chip color string for a task status.
   */
  getStatusColor: (status) => {
    const colors = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success'
    };
    return colors[status] || 'default';
  },

  /**
   * Get human-readable label for a task status.
   */
  getStatusLabel: (status) => {
    const labels = {
      PENDING: 'Pending',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed'
    };
    return labels[status] || status;
  },

  /**
   * Format a date string for display (e.g. "12 Apr 2026").
   */
  formatDate: (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  },

  /**
   * Format a datetime string for display (e.g. "12 Apr 2026, 14:30").
   */
  formatDateTime: (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }
};

export default TaskService;
